import React, { useEffect, useState, useRef } from "react";
import DOMPurify from "dompurify";
import { api, API_BASE } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Mail, Send, Paperclip, X, ArrowDownLeft, ArrowUpRight, FileDown } from "lucide-react";

const fileToBase64 = (file) => new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
});

export default function CommunicationThread({ withdrawalId, customerEmail }) {
    const { t } = useI18n();
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [reply, setReply] = useState("");
    const [files, setFiles] = useState([]);
    const [sending, setSending] = useState(false);
    const endRef = useRef(null);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/withdrawals/${withdrawalId}/emails`);
            setMessages(data);
        } finally { setLoading(false); }
    };

    useEffect(() => { load(); }, [withdrawalId]);

    useEffect(() => {
        if (endRef.current) endRef.current.scrollIntoView({ behavior: "smooth" });
    }, [messages.length]);

    const onFiles = async (fl) => {
        const arr = Array.from(fl || []);
        const next = [];
        for (const f of arr) {
            if (f.size > 5 * 1024 * 1024) {
                toast.error(t("toast_file_too_large"));
                continue;
            }
            const data = await fileToBase64(f);
            next.push({ filename: f.name, content_type: f.type || "application/octet-stream", data });
        }
        setFiles((existing) => [...existing, ...next].slice(0, 5));
    };

    const send = async () => {
        if (!reply.trim()) return;
        setSending(true);
        try {
            await api.post(`/withdrawals/${withdrawalId}/emails`, {
                body: reply,
                attachments: files,
            });
            toast.success(t("email_sent"));
            setReply(""); setFiles([]);
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || t("toast_submit_failed"));
        } finally { setSending(false); }
    };

    const downloadAtt = async (msgId, idx, name) => {
        const token = localStorage.getItem("wr_token");
        const res = await fetch(`${API_BASE}/withdrawals/${withdrawalId}/emails/${msgId}/attachments/${idx}`, {
            headers: { Authorization: `Bearer ${token}` },
        });
        const blob = await res.blob();
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = name || `attachment-${idx}`;
        a.click();
    };

    const fmtDate = (iso) => { try { return new Date(iso).toLocaleString("de-DE"); } catch { return iso; } };

    return (
        <div className="border border-gray-200 rounded-md bg-white p-6 mt-6" data-testid="email-thread">
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                    <Mail className="w-5 h-5 text-[#0b905d]" />
                    <h2 className="font-display text-lg font-semibold text-[#00153f]">{t("email_thread_title")}</h2>
                </div>
                <span className="text-xs font-mono text-gray-500">{customerEmail}</span>
            </div>

            {loading ? (
                <div className="text-sm text-gray-500">{t("loading")}</div>
            ) : messages.length === 0 ? (
                <div className="text-sm text-gray-500 py-6 text-center border border-dashed border-gray-200 rounded-md">
                    {t("email_thread_empty")}
                </div>
            ) : (
                <ul className="space-y-3 max-h-[480px] overflow-y-auto pr-1">
                    {messages.map((m, idx) => (
                        <li
                            key={m.id || idx}
                            data-testid={`email-msg-${idx}`}
                            className={`border rounded-md p-4 ${m.direction === "inbound" ? "border-blue-200 bg-blue-50/30" : "border-emerald-200 bg-emerald-50/30"}`}
                        >
                            <div className="flex items-center justify-between gap-2 mb-2">
                                <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-gray-500">
                                    {m.direction === "inbound"
                                        ? <><ArrowDownLeft className="w-3.5 h-3.5 text-blue-600" /> {t("email_inbound_label")}</>
                                        : <><ArrowUpRight className="w-3.5 h-3.5 text-[#0b905d]" /> {t("email_outbound_label")}</>}
                                    <span>·</span>
                                    <span className="normal-case font-sans">{m.from_email}</span>
                                </div>
                                <span className="text-xs text-gray-500">{fmtDate(m.sent_at || m.received_at || m.sort_at)}</span>
                            </div>
                            <div className="text-sm font-medium text-[#00153f] mb-2">{m.subject}</div>
                            {m.body_html ? (
                                <div className="text-sm text-gray-700 prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(m.body_html) }} />
                            ) : (
                                <div className="text-sm text-gray-700 whitespace-pre-wrap">{m.body_plain}</div>
                            )}
                            {Array.isArray(m.attachments) && m.attachments.length > 0 && (
                                <ul className="mt-3 space-y-1.5">
                                    {m.attachments.map((a, i) => (
                                        <li key={i} className="flex items-center justify-between border border-gray-200 rounded-md px-3 py-1.5 bg-white">
                                            <div className="flex items-center gap-2 text-xs">
                                                <Paperclip className="w-3.5 h-3.5 text-gray-500" />
                                                <span>{a.filename}</span>
                                                <span className="text-gray-400">({Math.round((a.size || 0) / 1024)} KB)</span>
                                            </div>
                                            <button type="button" onClick={() => downloadAtt(m.id, i, a.filename)} className="text-gray-500 hover:text-[#0b905d]" data-testid={`email-att-${idx}-${i}`}>
                                                <FileDown className="w-3.5 h-3.5" />
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </li>
                    ))}
                    <li ref={endRef} />
                </ul>
            )}

            {/* Reply editor */}
            <div className="mt-5 border-t border-gray-200 pt-4">
                <Textarea
                    rows={4}
                    placeholder={t("email_reply_placeholder")}
                    value={reply}
                    onChange={(e) => setReply(e.target.value)}
                    data-testid="email-reply-body"
                />
                {files.length > 0 && (
                    <ul className="mt-2 space-y-1">
                        {files.map((f, i) => (
                            <li key={i} className="flex items-center justify-between border border-gray-200 rounded-md px-2.5 py-1 text-xs">
                                <span className="flex items-center gap-1.5"><Paperclip className="w-3 h-3" /> {f.filename}</span>
                                <button type="button" onClick={() => setFiles((arr) => arr.filter((_, x) => x !== i))} className="text-gray-400 hover:text-red-600">
                                    <X className="w-3 h-3" />
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
                <div className="flex items-center justify-between mt-3">
                    <label className="text-xs text-gray-500 hover:text-[#00153f] cursor-pointer flex items-center gap-1" data-testid="email-reply-attach">
                        <Paperclip className="w-3.5 h-3.5" /> {t("email_attach")}
                        <input type="file" multiple className="hidden" onChange={(e) => onFiles(e.target.files)} />
                    </label>
                    <Button onClick={send} disabled={sending || !reply.trim()} className="bg-[#0b905d] hover:bg-[#067a4d] text-white" data-testid="email-send-btn">
                        <Send className="w-4 h-4 mr-2" /> {sending ? "..." : t("email_send")}
                    </Button>
                </div>
            </div>
        </div>
    );
}
